import type { Project } from "../model/Project";
import type { StampLibrary } from "../stamps/StampLibrary";
import { projectToSvg } from "./sceneToSvg";
import { triggerDownload } from "./download";
import { cssVar } from "../colors";

export interface RasterOptions {
  dpi?: number;
  format?: "png" | "jpeg";
}

export async function exportRaster(
  project: Project,
  library: StampLibrary,
  options: RasterOptions = {},
): Promise<Blob> {
  const dpi = options.dpi ?? 600;
  const format = options.format ?? "png";
  // Raster exports are a plain black-on-white line drawing — `monochrome`
  // collapses every CAM color to black and we paint a white background
  // below, so the result reads like a printed pattern sheet.
  // Notes are drawn (#157). A PNG / JPEG is a picture for a person to look at,
  // the same as a print — unlike SVG / DXF, nothing downstream will try to cut
  // the text. There is no Content dialog on this path, so no toggle either.
  const { svg, bounds } = projectToSvg(project, library, {
    monochrome: true,
    includeNotes: true,
  });

  const pxPerMm = dpi / 25.4;
  const width = Math.max(1, Math.ceil(bounds.w * pxPerMm));
  const height = Math.max(1, Math.ceil(bounds.h * pxPerMm));

  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    // White background for both formats — PNG would otherwise be
    // transparent and JPEG has no alpha at all. Black lines on white.
    ctx.fillStyle = cssVar("--export-raster-bg");
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    return await canvasToBlob(
      canvas,
      format === "jpeg" ? "image/jpeg" : "image/png",
      0.92,
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadRaster(
  project: Project,
  library: StampLibrary,
  options: RasterOptions = {},
) {
  const blob = await exportRaster(project, library, options);
  const ext = options.format ?? "png";
  const filename = `${sanitize(project.name)}.${ext}`;
  await triggerDownload(blob, filename);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      type,
      quality,
    );
  });
}

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9_-]/gi, "_") || "leather-art";
}
